import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCategoryTemplates,
  overrideCategory,
  setProductAttribute,
} from '../services/api';
import type { CategoryTemplate } from '../types';

/**
 * T078 [US5] Category hooks — TanStack React Query wrappers for
 * category templates and category override.
 */

// ─── Shopify Toast Helper ────────────────────────────────────

function showToast(message: string, isError = false): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shopify = (globalThis as any).shopify;
  if (typeof shopify?.toast?.show === 'function') {
    shopify.toast.show(message, { isError });
  }
}

// ─── Hooks ───────────────────────────────────────────────────

/** Fetch all category templates (cached — rarely changes). */
export function useCategoryTemplates(enabled = true) {
  return useQuery<CategoryTemplate[]>({
    queryKey: ['category-templates'],
    queryFn: getCategoryTemplates,
    enabled,
    staleTime: 1000 * 60 * 60, // 1 hour — templates rarely change
  });
}

/** Override the detected category for a product. */
export function useOverrideCategory(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categorySlug: string) =>
      overrideCategory(productId, { categorySlug }),
    onSuccess: (_data, categorySlug) => {
      // Invalidate product detail so it re-fetches with new category
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      showToast(`Category overridden to "${categorySlug}"`);
    },
    onError: (err: Error) => {
      showToast(err.message || 'Failed to override category', true);
    },
  });
}

/** Manually write a category attribute metafield for a product, then rescore. */
export function useSetProductAttribute(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value, label }: { key: string; value: string; label?: string }) =>
      setProductAttribute(productId, key, value, label),
    onSuccess: (data, { key }) => {
      // Update the product cache immediately from the PATCH response so
      // the saved value is visible without waiting for a Shopify re-fetch.
      queryClient.setQueryData<import('../types').ProductDetail>(
        ['product', productId],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            presentAttributes: data.presentAttributes,
            attributeValues: {
              ...(prev.attributeValues ?? {}),
              ...(data.attributeValues ?? {}),
            },
          };
        },
      );
      showToast(`"${key}" saved — product is rescoring`);
    },
    onError: (err: Error) => {
      showToast(err.message || 'Failed to save attribute', true);
    },
  });
}
